import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Skeleton } from '../../components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import {
  ScrollText, User, Building2, Clock, ChevronLeft, ChevronRight, Search, Download, Globe,
} from 'lucide-react';
import { ErrorBox } from '../../components/admin/AdminKit';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// WO-36 — the search box and the Export button now work, every entry shows its
// outcome and IP address, and admin actions (deletes, imports, plan changes…)
// are recorded by the server for every admin endpoint. Entries are kept 400 days.

const OUTCOME_STYLE = {
  ok: 'bg-green-500/10 text-green-600',
  denied: 'bg-red-500/10 text-red-600',
  refused: 'bg-amber-500/10 text-amber-600',
  error: 'bg-red-500/10 text-red-600',
};

function actionColor(action = '') {
  if (action.includes('failed')) return 'bg-red-500/10 text-red-600';
  if (action.includes('delete') || action.includes('clear') || action.includes('import')) return 'bg-red-500/10 text-red-500';
  if (action.includes('create') || action.includes('add')) return 'bg-green-500/10 text-green-600';
  if (action.includes('update') || action.includes('change') || action.includes('flag')) return 'bg-blue-500/10 text-blue-600';
  if (action.includes('login')) return 'bg-purple-500/10 text-purple-600';
  return 'bg-gray-500/10 text-gray-600';
}

function detailLine(log) {
  const m = log.metadata || {};
  const parts = [];
  if (m.method && m.path) parts.push(`${m.method} ${m.path}${m.query ? `?${m.query}` : ''}`);
  if (m.target_email) parts.push(`account ${m.target_email}`);
  if (m.before !== undefined || m.after !== undefined) parts.push(`${String(m.before)} → ${String(m.after)}`);
  if (m.email && !m.target_email) parts.push(m.email);
  return parts.join(' · ');
}

export default function AdminActivityLogs() {
  const { getToken } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({ action: '', outcome: '', search: '' });
  const [search, setSearch] = useState('');   // applied 400 ms after typing stops
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => { setSearch(filters.search.trim()); setPage(1); }, 400);
    return () => clearTimeout(t);
  }, [filters.search]);

  const params = useCallback((extra = {}) => {
    const p = new URLSearchParams(extra);
    if (filters.action) p.append('action', filters.action);
    if (filters.outcome) p.append('outcome', filters.outcome);
    if (search) p.append('search', search);
    return p;
  }, [filters.action, filters.outcome, search]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/analytics/audit/logs?${params({ page: String(page), page_size: '25' })}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) { setError(body.detail || `The server answered ${res.status}.`); return; }
      setError(null);
      setData(body);
    } catch (e) {
      setError('The server could not be reached.');
    } finally {
      setLoading(false);
    }
  }, [page, params, getToken]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await fetch(`${API_URL}/api/analytics/audit/logs/export?${params()}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) throw new Error(`The server answered ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `mid2bid_audit_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(`Export failed: ${e.message}`);
    } finally {
      setExporting(false);
    }
  };

  if (loading && !data && !error) {
    return (
      <div className="space-y-6" data-testid="admin-activity-logs-loading">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-12 w-full" />
        <div className="space-y-2">{[...Array(10)].map((_, i) => <Skeleton key={i} className="h-16" />)}</div>
      </div>
    );
  }

  const logs = data?.logs || [];
  const totalPages = data?.total_pages || 1;
  const actionTypes = data?.filters?.action_types || [];

  return (
    <div className="space-y-6" data-testid="admin-activity-logs-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">Activity Logs & Audit Trail</h1>
          <p className="text-muted-foreground">
            Who did what, when and from where. Every admin action is recorded; entries are kept {data?.retention_days || 400} days.
          </p>
        </div>
        <Button variant="outline" className="gap-2" onClick={exportCsv} disabled={exporting} data-testid="audit-export">
          <Download className="w-4 h-4" />
          {exporting ? 'Exporting…' : 'Export CSV'}
        </Button>
      </div>

      {error && <ErrorBox message={error} onRetry={fetchData} />}

      <Card className="border-0 bg-card/50">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search action, record, e-mail or IP…"
                  className="pl-9"
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  data-testid="audit-search"
                />
              </div>
            </div>
            <Select value={filters.action || 'all'}
              onValueChange={(v) => { setFilters({ ...filters, action: v === 'all' ? '' : v }); setPage(1); }}>
              <SelectTrigger className="w-[220px]"><SelectValue placeholder="All actions" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All actions</SelectItem>
                {actionTypes.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filters.outcome || 'all'}
              onValueChange={(v) => { setFilters({ ...filters, outcome: v === 'all' ? '' : v }); setPage(1); }}>
              <SelectTrigger className="w-[180px]"><SelectValue placeholder="Any outcome" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Any outcome</SelectItem>
                <SelectItem value="ok">Done</SelectItem>
                <SelectItem value="denied">Denied (not allowed)</SelectItem>
                <SelectItem value="refused">Refused</SelectItem>
                <SelectItem value="error">Failed</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card className="border-0 bg-card/50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><ScrollText className="w-5 h-5 text-primary" />Activity log</CardTitle>
          <CardDescription>{(data?.total || 0).toLocaleString('en-US')} entries match</CardDescription>
        </CardHeader>
        <CardContent>
          {logs.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <ScrollText className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>No entry matches these filters</p>
            </div>
          ) : (
            <div className="space-y-2" data-testid="audit-rows">
              {logs.map((log, i) => (
                <div key={log.id || i} className="p-4 rounded-lg bg-muted/30">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <Badge className={actionColor(log.action)}>{log.action}</Badge>
                        {log.outcome && <Badge className={OUTCOME_STYLE[log.outcome] || ''}>{log.outcome}</Badge>}
                      </div>
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1"><User className="w-3 h-3" />{log.user}{log.user_email && log.user_email !== log.user ? ` (${log.user_email})` : ''}</span>
                        <span className="flex items-center gap-1"><Building2 className="w-3 h-3" />{log.company}</span>
                        {log.ip_address && <span className="flex items-center gap-1"><Globe className="w-3 h-3" />{log.ip_address}</span>}
                      </div>
                      {detailLine(log) && <p className="text-xs text-muted-foreground mt-1 break-all">{detailLine(log)}</p>}
                    </div>
                    <div className="flex items-center gap-1 text-sm text-muted-foreground shrink-0">
                      <Clock className="w-3 h-3" />
                      {log.timestamp ? new Date(log.timestamp).toLocaleString() : '—'}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-6 pt-4 border-t">
              <p className="text-sm text-muted-foreground">Page {page} of {totalPages}</p>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages}>
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
